import { Component, Inject, Input, OnDestroy, OnInit } from '@angular/core';
import { ClientHomePage } from '../../../models/client';
import {
  PayDateInfoEx,
  PayTypes,
  PayrollStartData,
  PayrollStatuses
} from '../../../models/payroll';
import { PayrollService } from '../../../services/payroll.service';
import { CompaniesService } from '../../../services/companies.service';
import { PayrollManagement } from '../../../tools/payroll-management';
import { InstanceTypes } from '../../../models/instance-types';
import { RouteParamsService } from '../../../services/route-params.service';
import { Subject, Subscription } from 'rxjs';
import { AuthService } from '../../../services/auth.service';
import { Store } from '@ngxs/store';
import { takeUntil } from 'rxjs/operators';
import { ClientSelectionState } from '../../../state/ClientSelectionState/ClientSelection.state';
import { AclService } from '../../../services/acl.service';

@Component({
  selector: 'lib-todo-tile',
  templateUrl: './todo-tile.component.html',
  styleUrls: ['./todo-tile.component.scss'],
  standalone: false
})
export class TodoTileComponent implements OnInit, OnDestroy {
  @Input()
  public homePageData: ClientHomePage;
  public canAccessPayroll: boolean;
  @Input()
  public canAccessPeople: boolean;
  @Input()
  public canManageUsers: boolean;

  public payrollStartData: PayrollStartData;
  public daysUntilNextPayrollSubmission: number;
  public dateUntilNextPayrollSubmission: string;
  public payrollRoute: string;
  public baseUrl: string;
  public clientCode: string;
  public payrollButtonText = 'Run Payroll';
  private destroy$ = new Subject<void>();
  private clientCodeSubscription: Subscription;
  get payrollTileText() {
    if (this.daysUntilNextPayrollSubmission === 0 || this.daysUntilNextPayrollSubmission < 0) {
      return 'Payroll Due Today';
    }
    return `Due ${this.dateUntilNextPayrollSubmission}`;
  }
  constructor(
    private payrollService: PayrollService,
    private companyService: CompaniesService,
    private payrollMgmt: PayrollManagement,
    private authService: AuthService,
    private store: Store,
    private aclService: AclService,
    @Inject('env') private env: any
  ) {}
  ngOnInit(): void {
    this.store
      .select(ClientSelectionState.clientCode)
      .pipe(takeUntil(this.destroy$))
      .subscribe(code => {
        if (code != null) {
          this.clientCode = code;
          const userId = this.authService.user.UserId;
          this.aclService.HasMinimumRoleByName(userId, 'CSR1').subscribe(result => {
            if (result) {
              this.canAccessPayroll = true;
              this.init(code);
            } else {
              this.aclService
                .AccessCheck(userId, 'Client Portal Admin', 'RunPayroll', code)
                .subscribe(checkResult => {
                  this.canAccessPayroll = checkResult;
                  this.init(code);
                });
            }
          });
        }
      });
  }

  private init(code: string) {
    if (this.canAccessPayroll) {
      this.payrollService.getPayrollStartData(code).subscribe(payrollStartData => {
        this.payrollStartData = payrollStartData;
        this._setDateInfoLeftForPayroll();
      });
      this.baseUrl = this.env.instanceType === InstanceTypes.ClientPortal ? '' : '/client';
      this.payrollRoute = `/client/${code}/payroll-landing`;
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
  }

  private _setDateInfoLeftForPayroll(): void {
    // FIXME: will need addressed when multiple locations are enabled
    let earliestPayDate: PayDateInfoEx;

    if (
      this.payrollStartData?.ActivePayroll &&
      this.payrollStartData.ActivePayroll.PayType != PayTypes.Manual
    ) {
      var location = this.payrollStartData.Locations.find(
        x => x.Id == this.payrollStartData.ActivePayroll.LocationId
      );

      earliestPayDate = location.NextPayDates.find(
        x => x.CalendarId == location.Calendars[0].CalendarId
      );
      this.payrollButtonText = 'Resume Payroll';
      if (
        this.payrollStartData.ActivePayroll.Status == PayrollStatuses.Started &&
        !this.authService.isCurrentUser(this.payrollStartData.ActivePayroll.LastEditBy)
      ) {
        this.payrollButtonText = 'Payroll In Progress';
      }
    } else {
      this.payrollButtonText = 'Run Payroll';
      earliestPayDate = this.payrollStartData.Locations[0].NextPayDates?.reduce((a, b) => {
        return new Date(a.ProcessDueDate) < new Date(b.ProcessDueDate) ? a : b;
      });
    }

    // get days left
    const difference = +new Date(earliestPayDate.ProcessDueDate) - +new Date();

    // if the due date is today or passed, then display 0
    this.daysUntilNextPayrollSubmission =
      difference >= 0 ? Math.floor(difference / (1000 * 3600 * 24)) : 0;
    this.dateUntilNextPayrollSubmission = new Date(
      earliestPayDate.ProcessDueDate
    ).toLocaleDateString('en-us', { day: 'numeric', month: 'numeric', year: 'numeric' });
  }

  protected readonly PayrollStatuses = PayrollStatuses;
}
